import { createRootRoute, Link, Outlet } from '@tanstack/react-router'
import { useEffect } from 'react'
import { useAuthStore } from '@/store/auth'
import { authApi, hasLocalAuthEvidence } from '@/lib/api'
import { Button } from '@/components/ui/button'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import { User, LogOut, Menu } from 'lucide-react'

export const Route = createRootRoute({
  component: RootComponent,
})

/** 根布局：提供全局 Header 与统一内容容器。 */
function RootComponent() {
  const user = useAuthStore((s) => s.user)
  const isAuthenticated = useAuthStore((s) => s.isAuthenticated)
  const logout = useAuthStore((s) => s.logout)
  const setUser = useAuthStore((s) => s.setUser)
  const logoTo = user ? '/projects' : '/'

  /**
   * 会话引导：校验当前登录态并同步用户信息。
   *
   * 以下场景直接跳过，避免无意义请求 → 401 → 被动跳登录：
   *   1. 在登录页且明确未登录（正常的"尚未登录"状态）；
   *   2. zustand 内存中未登录 + localStorage persist 中也没有 token 痕迹
   *      （例如首次打开首页 `/`、隐私模式等冷启动场景）。
   *
   * 其余场景（F5 刷新受保护页、或 zustand 还没 rehydrate 完成但 localStorage 已有 token）
   * 都需要调 /auth/verify，用 Cookie 或 localStorage 中保存的 token 向服务器确认身份，
   * 再把用户信息同步回 zustand，确保后续路由需要 user/role 时数据已就绪。
   */
  useEffect(() => {
    // 场景 1：登录页 + 未登录
    if (window.location.pathname === '/login' && !isAuthenticated) return
    // 场景 2：完全冷启动，没有任何本地登录证据（既无内存态也无 persist 残留）
    if (!isAuthenticated && !hasLocalAuthEvidence()) return

    let canceled = false
    authApi.verify()
      .then((res) => {
        if (canceled) return
        if (res.success && res.data?.user) setUser(res.data.user)
      })
      .catch(() => {
        if (canceled) return
        // verify 失败：要么 token 无效/过期，要么用户已被禁用
        // 清理本地态并跳登录；在登录页本身时不重复跳
        logout()
        if (window.location.pathname !== '/login') window.location.href = '/login'
      })

    return () => {
      canceled = true
    }
  }, [logout, setUser, isAuthenticated])
  
  /** 退出登录：清理本地 token 并跳转到登录页。 */
  const handleLogout = async () => {
    if (import.meta.env.DEV) console.debug('[Auth] 退出登录')
    try {
      await authApi.logout()
    } catch {
    }
    logout()
    window.location.href = '/login'
  }
  
  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <header className="border-b bg-card">
        <div className="container mx-auto px-4 h-16 flex items-center justify-between">
          <div className="flex items-center gap-8">
            <Link to={logoTo} className="flex items-center">
              <img src="/logo.png" alt="熊猫译社" className="h-10 w-auto" />
            </Link>
            
            {user && (
              <nav className="hidden md:flex items-center gap-4">
                <Link
                  to="/projects"
                  className="text-sm font-medium text-muted-foreground hover:text-primary transition-colors data-[status=active]:text-foreground data-[status=active]:font-semibold"
                >
                  项目
                </Link>
                {(user.role === 'founder' || user.role === 'pm') && (
                  <Link
                    to="/clients"
                    className="text-sm font-medium text-muted-foreground hover:text-primary transition-colors data-[status=active]:text-foreground data-[status=active]:font-semibold"
                  >
                    客户
                  </Link>
                )}
                {(user.role === 'founder' || user.role === 'pm') && (
                  <>
                    <Link
                      to="/archive"
                      className="text-sm font-medium text-muted-foreground hover:text-primary transition-colors data-[status=active]:text-foreground data-[status=active]:font-semibold"
                    >
                      归档
                    </Link>
                    <Link
                      to="/analytics"
                      className="text-sm font-medium text-muted-foreground hover:text-primary transition-colors data-[status=active]:text-foreground data-[status=active]:font-semibold"
                    >
                      数据
                    </Link>
                  </>
                )}
                {(user.role === 'founder' || user.role === 'pm') && (
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <button className="text-sm font-medium hover:text-primary transition-colors flex items-center gap-1">
                        系统管理
                      </button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="start">
                      {user.role === 'founder' && (
                        <DropdownMenuItem asChild>
                          <Link to="/admin/users" className="cursor-pointer">用户管理</Link>
                        </DropdownMenuItem>
                      )}
                      {user.role === 'founder' && (
                        <DropdownMenuItem asChild>
                          <Link to="/admin/llm" className="cursor-pointer">LLM 配置</Link>
                        </DropdownMenuItem>
                      )}
                      <DropdownMenuItem asChild>
                        <Link to="/admin/style" className="cursor-pointer">全局风格库</Link>
                      </DropdownMenuItem>
                    </DropdownMenuContent>
                  </DropdownMenu>
                )}
              </nav>
            )}
          </div>
          
          {user ? (
            <div className="flex items-center gap-2">
              {/* 移动端汉堡菜单：右上角，替换小人图标，合并导航 + 用户信息 + 退出 */}
              <div className="md:hidden">
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button variant="ghost" size="icon" aria-label="打开菜单">
                      <Menu className="h-5 w-5" />
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end" className="w-56">
                    <DropdownMenuLabel className="font-normal">
                      <div className="flex flex-col space-y-1">
                        <p className="text-sm font-medium leading-none">{user.name}</p>
                        <p className="text-xs leading-none text-muted-foreground">{user.email}</p>
                      </div>
                    </DropdownMenuLabel>
                    <DropdownMenuSeparator />
                    <DropdownMenuItem asChild>
                      <Link to="/projects" className="cursor-pointer">项目</Link>
                    </DropdownMenuItem>
                    {(user.role === 'founder' || user.role === 'pm') && (
                      <DropdownMenuItem asChild>
                        <Link to="/clients" className="cursor-pointer">客户</Link>
                      </DropdownMenuItem>
                    )}
                    {(user.role === 'founder' || user.role === 'pm') && (
                      <>
                        <DropdownMenuItem asChild>
                          <Link to="/archive" className="cursor-pointer">归档</Link>
                        </DropdownMenuItem>
                        <DropdownMenuItem asChild>
                          <Link to="/analytics" className="cursor-pointer">数据</Link>
                        </DropdownMenuItem>
                      </>
                    )}
                    {(user.role === 'founder' || user.role === 'pm') && (
                      <>
                        <DropdownMenuSeparator />
                        {user.role === 'founder' && (
                          <DropdownMenuItem asChild>
                            <Link to="/admin/users" className="cursor-pointer">用户管理</Link>
                          </DropdownMenuItem>
                        )}
                        {user.role === 'founder' && (
                          <DropdownMenuItem asChild>
                            <Link to="/admin/llm" className="cursor-pointer">LLM 配置</Link>
                          </DropdownMenuItem>
                        )}
                        <DropdownMenuItem asChild>
                          <Link to="/admin/style" className="cursor-pointer">全局风格库</Link>
                        </DropdownMenuItem>
                      </>
                    )}
                    <DropdownMenuSeparator />
                    <DropdownMenuItem onClick={handleLogout} className="cursor-pointer">
                      <LogOut className="mr-2 h-4 w-4" />
                      退出登录
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              </div>

              {/* 桌面端用户菜单：仅 md 及以上显示，移动端已并入汉堡菜单 */}
              <div className="hidden md:block">
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button
                      variant="ghost"
                      className="relative h-8 w-8 rounded-full"
                      aria-label="打开用户菜单"
                    >
                      <User className="h-4 w-4" />
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent className="w-56" align="end" forceMount>
                    <DropdownMenuLabel className="font-normal">
                      <div className="flex flex-col space-y-1">
                        <p className="text-sm font-medium leading-none">{user.name}</p>
                        <p className="text-xs leading-none text-muted-foreground">{user.email}</p>
                      </div>
                    </DropdownMenuLabel>
                    <DropdownMenuSeparator />
                    <DropdownMenuItem onClick={handleLogout} className="cursor-pointer">
                      <LogOut className="mr-2 h-4 w-4" />
                      退出登录
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              </div>
            </div>
          ) : null}
        </div>
      </header>
      
      {/* Main Content */}
      <main className="container mx-auto px-4 py-6">
        <Outlet />
      </main>
    </div>
  )
}
